import React, {Component, Fragment} from 'react'
import PropTypes from 'prop-types'
import Creatable from 'react-select/lib/Creatable'
import {Label} from '@cred/react-label'
import classNames from 'classnames'
import FormField from './FormField'
import defaultStyles from './themes/default/styles.module.scss'

const getStyles = meta => ({
  singleValue: (provided, state) => {
    let styles = {
      ...provided,
      margin: 0,
      color: 'rgba(0, 0, 0, 0.65)',
      transition: 'color 0.2s ease-in-out'
    }

    const hasValue = state.getValue().every(o => !!o.value)

    if (hasValue) {
      styles = {...styles, paddingTop: 15}
    }

    return styles
  },
  control: (provided, state) => {
    let styles = {
      ...provided,
      width: '100%',
      height: 57,
      background: '#ffffff',
      border: 'solid 1px rgba(0, 0, 0, 0.3)',
      boxShadow: 'none',
      cursor: 'pointer',
      zIndex: 101
    }

    const hasValue = state.getValue().every(o => !!o.value)

    if (state.hasValue) {
      styles = {...styles, borderColor: '#4099d3'}
    }

    if (state.isFocused || hasValue) {
      styles = {...styles, border: 'solid 1px rgb(64, 153, 211)'}
    }

    if (meta.touched && meta.error) {
      styles = {...styles, border: 'solid 1px #fd5653'}
    }

    return styles
  },
  valueContainer: provided => ({...provided, padding: '0 16px'}),
  placeholder: provided => ({
    ...provided,
    fontSize: 16,
    color: 'rgba(0, 0, 0, 0.5)',
    transition: 'color 0.2s ease-in-out'
  }),
  clearIndicator: provided => ({...provided, padding: '8px 0 8px 4px'}),
  indicatorSeparator: () => ({display: 'none'}),
  dropdownIndicator: provided => ({
    ...provided,
    padding: '8px 12px 8px 4px',
    color: 'black',
    width: 40,
    backgroundImage: 'url("@cred/global-assets/images/misc/chevron-down.svg")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center center',
    backgroundSize: 12,
    boxSizing: 'border-box'
  }),
  menu: provided => ({
    ...provided,
    margin: 0,
    minWidth: 100,
    maxHeight: 250,
    overflowY: 'auto',
    right: 0,
    boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.1)',
    border: 'solid 1px rgb(64, 153, 211)',
    borderTop: 'solid 1px rgba(181, 191, 199, 0.3)',
    borderRadius: '0 0 4px 4px',
    zIndex: 103
  }),
  menuList: provided => ({
    ...provided,
    padding: 0,
    maxHeight: '100%',
    zIndex: 102
  }),
  group: provided => ({
    ...provided,
    borderBottom: 'solid 1px rgba(181, 191, 199, 0.3)',
    padding: '16px 0'
  }),
  groupHeading: provided => ({
    ...provided,
    fontSize: 14,
    textTransform: 'initial',
    color: 'rgba(0, 0, 0, 0.5)',
    marginBottom: 5,
    padding: '0 23px'
  }),
  option: (provided, state) => {
    let styles = {
      ...provided,
      padding: '8px 16px',
      fontSize: 16,
      color: 'rgba(0, 0, 0, 0.65)',
      cursor: 'pointer',
	    textAlign: 'left'
    }

    if (state.isSelected) {
      styles = {
        ...styles,
        backgroundColor: '#4099d3',
        color: '#ffffff'
      }
    }

    return styles
  }
})

class SelectCustom extends Component {
  static propTypes = {
    options: PropTypes.array.isRequired,
    input: PropTypes.object.isRequired,
    meta: PropTypes.object.isRequired,
    placeholder: PropTypes.string,
    onChangeHandle: PropTypes.func,
    className: PropTypes.string,
    classNamePrefix: PropTypes.string,
    multi: PropTypes.bool,
    clearable: PropTypes.bool,
    searchable: PropTypes.bool,
    label: PropTypes.string
  }

  static defaultProps = {
    className: 'select-custom__container',
    classNamePrefix: 'select-custom',
    multi: false,
    clearable: false,
    searchable: false,
    label: '',
    placeholder: 'Select a value',
    onChangeHandle: () => {}
  }

  state = {isOpen: false}

  node = React.createRef()

  singleChangeHandler = func => value => {
    const {onChangeHandle} = this.props

    this.setState({isOpen: false})

    func(value ? value.value : '')
    onChangeHandle()
  }

  multiChangeHandler = func => values => {
    const {onChangeHandle} = this.props

    this.setState({isOpen: false})

    func(values.map(value => value.value))
    onChangeHandle()
  }

  transformValue = (value, options, multi) => {
    if (multi && typeof value === 'string') {
      return []
    }
    const filteredOptions = options.filter(option =>
      multi ? value.indexOf(option.value) !== -1 : option.value === value
    )

    return multi ? filteredOptions : filteredOptions[0]
  }

  clickOverlay = () => {
    this.setState({isOpen: false})
    this.node.current.onMenuClose()
  }

  selectClick = () => {
    const {input: {onBlur, value}} = this.props

    onBlur(value)
    this.setState(prevState => ({isOpen: !prevState.isOpen}))
  }

  render() {
    const {
        input,
        options,
        multi,
        className,
        classNamePrefix,
        clearable,
        searchable,
        placeholder,
        meta,
        label
      } = this.props,
      {isOpen} = this.state,
      {name, value, onChange} = input

    let dropdownOptions = []

    if (
      searchable &&
      value &&
      !options.some(({value: optionValue}) => value === optionValue)
    ) {
      dropdownOptions = [{label: value, value}, ...options]
    } else {
      dropdownOptions = options
    }

    const dropDownClassName = classNames(className, {'select-custom__without-label': !label}),
      transformedValue = this.transformValue(value, dropdownOptions, multi)

    return (
      <Fragment>
        {isOpen && <div className={defaultStyles.overlay} onClick={this.clickOverlay}/>}
        <FormField
          status={meta}
          error={meta.error}
          placeholder={placeholder}
          value={input.value}
          label={label}
        >
          <Creatable
            styles={getStyles(meta)}
            createOptionPosition="first"
            ref={this.node}
            onMenuOpen={this.selectClick}
            valueKey="value"
            name={name}
            value={transformedValue}
            isMulti={multi}
            isClearable={clearable}
            isSearchable={searchable}
            placeholder={placeholder}
            options={dropdownOptions}
            onChange={
              multi
                ? this.multiChangeHandler(onChange)
                : this.singleChangeHandler(onChange)
            }
            className={dropDownClassName}
            classNamePrefix={classNamePrefix}
          />
          <Label className={defaultStyles.floatingLabel}>{label}</Label>
        </FormField>
      </Fragment>
    )
  }
}

export {SelectCustom, getStyles}
export default SelectCustom
